import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Crown, User } from "lucide-react";
import api from "@/lib/api";
import { PageHero } from "@/components/Shared";
import { useSiteContent } from "@/lib/siteContent";

export default function HallOfChampions() {
  const [champs, setChamps] = useState([]);
  const content = useSiteContent();
  useEffect(() => {
    api.get("/champions").then((r) => setChamps(r.data)).catch(() => {});
  }, []);
  const hero = content?.["hall-of-legends"]?.hero_image || "";

  const src = (p) => (p ? (p.startsWith("http") ? p : `${process.env.REACT_APP_BACKEND_URL}${p}`) : "");

  return (
    <div data-testid="hall-of-champions-page">
      <PageHero
        title="Hall of Legends"
        subtitle="Every Legends season crowns one champion. These are the players who topped the standings and etched their name into league history."
        image={content ? (src(hero) || "https://images.pexels.com/photos/7005552/pexels-photo-7005552.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=1600") : ""}
        testId="champions-hero"
      />
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24">
        {champs.length === 0 ? (
          <p className="text-slate-400 text-center py-20">No champions crowned yet — the first season is still in play.</p>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {champs.map((c, i) => (
              <Link key={c.season} to={`/players/${encodeURIComponent(c.name)}`} data-testid={`champion-card-${i}`}
                className="group relative bg-navy-paper border border-slate-800 hover:border-gold/60 transition-all duration-300 hover:-translate-y-1 overflow-hidden animate-fade-up" style={{ animationDelay: `${i * 90}ms` }}>
                <div className="relative aspect-[4/5] overflow-hidden bg-navy">
                  {src(c.picture) ? (
                    <img src={src(c.picture)} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><User className="w-20 h-20 text-slate-700" /></div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/30 to-transparent" />
                  <Crown className="absolute top-4 right-4 w-7 h-7 text-gold drop-shadow-[0_0_10px_rgba(212,175,55,0.6)]" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <div className="text-gold text-[10px] uppercase tracking-[0.3em] mb-1">{c.season}</div>
                    <h3 className="font-display text-2xl font-bold text-white">{c.name}</h3>
                    <p className="text-slate-300 text-sm mt-1">{c.points} pts · {c.wins} win{c.wins === 1 ? "" : "s"}</p>
                  </div>
                </div>
                {(c.quote || c.story) && (
                  <div className="p-5 border-t border-gold/20" data-testid={`champion-story-card-${i}`}>
                    {c.quote && <p className="font-display italic text-gold text-lg leading-snug">“{c.quote}”</p>}
                    {c.story && <p className="text-slate-300 text-sm leading-relaxed mt-3">{c.story}</p>}
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
